import { AGE_GROUPS, type AgeGroup } from "../domain/drill";

const STORAGE_KEY = "puckly-coach-practice-plan-v1";

export type PracticeStation = {
  drillId: string;
  title: string;
  durationMinutes: number;
  description: string;
  descriptionSize: "small" | "normal" | "large";
  focus: string;
  focusSize: "small" | "normal" | "large";
};

export type PracticePlan = {
  id: string;
  team: string;
  ageGroup: AgeGroup;
  date: string;
  startTime: string;
  endTime: string;
  theme: string;
  equipment: string;
  stations: PracticeStation[];
  updatedAt: string;
};

export const emptyStation = (index: number): PracticeStation => ({
  drillId: "",
  title: `Station ${index + 1}`,
  durationMinutes: 15,
  description: "",
  descriptionSize: "normal",
  focus: "",
  focusSize: "normal",
});

export const emptyPlan = (): PracticePlan => ({
  id: "current-practice",
  team: "Puckly practice",
  ageGroup: "U10",
  date: new Date().toISOString().slice(0, 10),
  startTime: "",
  endTime: "",
  theme: "",
  equipment: "",
  stations: [emptyStation(0), emptyStation(1), emptyStation(2)],
  updatedAt: new Date().toISOString(),
});

export function practiceDurationMinutes(
  startTime: string,
  endTime: string,
  fallbackMinutes: number,
) {
  if (!startTime || !endTime) return fallbackMinutes;
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);
  if (
    !Number.isFinite(startHours) ||
    !Number.isFinite(startMinutes) ||
    !Number.isFinite(endHours) ||
    !Number.isFinite(endMinutes)
  )
    return fallbackMinutes;
  const start = startHours * 60 + startMinutes;
  const end = endHours * 60 + endMinutes;
  const duration = end >= start ? end - start : 24 * 60 - start + end;
  return duration || fallbackMinutes;
}

export function normalizePlan(value: Partial<PracticePlan>): PracticePlan {
  const fallback = emptyPlan();
  const savedStations = Array.isArray(value.stations) ? value.stations : [];
  const stationCount = Math.max(3, savedStations.length);
  return {
    ...fallback,
    ...value,
    id: value.id || fallback.id,
    ageGroup: AGE_GROUPS.includes(value.ageGroup as AgeGroup)
      ? (value.ageGroup as AgeGroup)
      : fallback.ageGroup,
    stations: Array.from({ length: stationCount }, (_, index) => ({
      ...emptyStation(index),
      ...savedStations[index],
    })),
    updatedAt:
      value.updatedAt && Number.isFinite(Date.parse(value.updatedAt))
        ? value.updatedAt
        : fallback.updatedAt,
  };
}

export function loadPlan(
  storage: Pick<Storage, "getItem"> = localStorage,
): PracticePlan {
  try {
    const saved = storage.getItem(STORAGE_KEY);
    return saved
      ? normalizePlan(JSON.parse(saved) as Partial<PracticePlan>)
      : emptyPlan();
  } catch {
    return emptyPlan();
  }
}

export function savePlan(
  plan: PracticePlan,
  storage: Pick<Storage, "setItem"> = localStorage,
) {
  const normalized = normalizePlan(plan);
  storage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}
